import {FeatureRequest, FeatureRequestStatusList} from "../../../models/support";
import {useEffect, useState} from "react";
import {adminFetchFeatureRequest, fetchFeatureRequest} from "../../../components/business_logic/feature_request_requests";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

interface PreviewFeatureRequestProps {
    request: FeatureRequest;
    isAdmin: boolean;
}

export default function PreviewFeatureRequest({request, isAdmin}: PreviewFeatureRequestProps) {
    const [fullRequest, setFullRequest] = useState({} as FeatureRequest);
    
    useEffect(() => {
        if (isAdmin) {
            adminFetchFeatureRequest(request).then(r => {
                if (r && Object.keys(r).length > 0) {
                    setFullRequest(r);
                }
            })
        } else {
            fetchFeatureRequest(request).then(r => {
                if (r && Object.keys(r).length > 0) {
                    setFullRequest(r);
                }
            })
        }
    }, [request, isAdmin])

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatStatus = (status?: string) => {
        if (!status) return 'N/A';
        const statusOption = FeatureRequestStatusList.find(s => s.value === status);
        return statusOption ? statusOption.label : status.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const displayRequest = fullRequest && Object.keys(fullRequest).length > 0 ? fullRequest : request;

    return (
        <Container 
            id="feature-request-preview"
            sx={{
                pt: { xs: 4, sm: 6 },
                pb: { xs: 4, sm: 6 },
                px: { xs: 3, sm: 4 },
                maxWidth: '800px',
                margin: '0 auto'
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography 
                    component="h1" 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    {displayRequest.title || 'Untitled Feature Request'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Metadata */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 3,
                        flexWrap: 'wrap',
                        mb: 2
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        <strong>Status:</strong> {formatStatus(displayRequest.request_status)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Created:</strong> {formatDate(displayRequest.created_at)}
                    </Typography>
                    {displayRequest.updated_at && displayRequest.updated_at !== displayRequest.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Updated:</strong> {formatDate(displayRequest.updated_at)}
                        </Typography>
                    )}
                    {!displayRequest.anonymous && displayRequest.user && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {displayRequest.user.first_name + " " +  displayRequest.user.last_name
                                || displayRequest.user.email || 'N/A'}
                        </Typography>
                    )}
                    {displayRequest.anonymous && (
                        <Chip 
                            label="Anonymous" 
                            size="small" 
                            color="default"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>

            {/* Description Section */}
            <Box 
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: { xs: 3, sm: 4 },
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Description
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {displayRequest.description ? (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: 'text.primary',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {displayRequest.description}
                    </Typography>
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Loading description...
                    </Typography>
                )}
            </Box>
        </Container>
    );
}
